import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Tag, TagType } from '@bringg/react-components';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import { useUserTypeModal } from '../user-type-modal-context';

export const useGetUserTypesTableColumns = () => {
	const { openEditModal } = useUserTypeModal();
	const { t } = useTranslation();

	const columns = useMemo(() => {
		return [
			{
				sortDirections: ['descend', 'ascend'],
				title: t('USER_TYPE.ID'),
				dataIndex: 'id',
				sorter: (a, b) => a.id - b.id,
				render: (id: number) => <span>{id}</span>
			},
			{
				sortDirections: ['descend', 'ascend'],
				title: t('USER_TYPE.TITLE'),
				sorter: (a, b) => a.title.localeCompare(b.title),
				dataIndex: 'title',
				width: '30%',
				render: (title, userType) => {
					return (
						<span>
							{title}
							{userType.default_for_merchant ? (
								<Tag type={TagType.info} className="default-for-merchant-tag">
									{t('USER_TYPE.DEFAULT_FOR_MERCHANT_LABEL')}
								</Tag>
							) : null}
						</span>
					);
				}
			},
			{
				sortDirections: ['descend', 'ascend'],
				title: t('USER_TYPE.MAXIMUM_TASKS_PER_SHIFT'),
				sorter: (a, b) => a.maximum_tasks_per_shift - b.maximum_tasks_per_shift,
				dataIndex: 'maximum_tasks_per_shift',
				align: 'right',
				width: 300,
				render: text => {
					return <span data-test-id="maximumTasksPerShift">{text}</span>;
				}
			},
			{
				sortDirections: ['descend', 'ascend'],
				title: t('USER_TYPE.COST_PER_TASK'),
				sorter: (a, b) => a.cost_per_task - b.cost_per_task,
				dataIndex: 'cost_per_task',
				align: 'right',
				width: 300,
				render: text => {
					return <span data-test-id="costPerTask">{text}</span>;
				}
			},
			{
				sortDirections: ['descend', 'ascend'],
				title: t('USER_TYPE.SPEED_FACTOR'),
				sorter: (a, b) => a.speed_factor - b.speed_factor,
				dataIndex: 'speed_factor',
				align: 'right',
				width: 300,
				render: text => {
					return <span data-test-id="speedFactor">{text}</span>;
				}
			},
			{
				title: t('USER_TYPE.EDIT_ACTION'),
				dataIndex: 'id',
				width: 300,
				responsive: ['lg', 'xl', 'xxl'],
				render: (id: number) => (
					<Button
						type="link"
						icon={<BringgIcon iconName={BringgFontIcons.Pencil} />}
						onClick={() => openEditModal(id)}
					>
						{t('USER_TYPE.EDIT')}
					</Button>
				)
			}
		];
	}, [openEditModal, t]);

	return columns;
};
